import React, { ChangeEvent } from 'react';
import { Checkbox, Col, Radio, Row, Tooltip } from 'antd';

import { SendCommunicationChannels, SendCommunicationCustomersSelection } from 'types';
import CardSendCommunication from 'components/molecules/card-send-communication';
import { useIntl } from 'common/config/i18n';

import {
  Props,
  SubTitle,
  getMinTimeForCommunication,
  StyledDivider,
  ErrorMessage,
  isTimeBefore,
  TimeSelect,
  NumberDaysInput,
} from './utils';

const TargetChannels = ({
  formik,
  customersSelection,
  handleChannelChange,
  channelsArePresent,
  elemRef,
  hasTargetChannels,
  canWriteDefferedCommunications,
  maxDaysBeforeDeferredSending,
  targetChannelsHeight,
}: Props) => {
  const { t } = useIntl();

  const isPastCommunication = maxDaysBeforeDeferredSending == undefined || maxDaysBeforeDeferredSending < 0;
  const defferedDaysBeforeCirculationMin = 0;
  const defferedDaysBeforeCirculationMax = maxDaysBeforeDeferredSending ?? 30;

  const disableDifferedCommunication =
    !canWriteDefferedCommunications ||
    isPastCommunication ||
    customersSelection === SendCommunicationCustomersSelection.DVNumbers;

  const minDefferedTime =
    maxDaysBeforeDeferredSending === formik.values.differedDaysBeforeCirculation
      ? getMinTimeForCommunication()
      : '00:00';

  const renderDefferedTooltipMessage = () => {
    let toolTipMessage = '';
    if (customersSelection === SendCommunicationCustomersSelection.DVNumbers) {
      toolTipMessage = t('sendCommunication', 'differedCommunication', 'dvNumbersTarget');
    }
    if (!canWriteDefferedCommunications) {
      toolTipMessage = t('sendCommunication', 'differedCommunication', 'missingAuth');
    } else if (isPastCommunication) {
      toolTipMessage = t('sendCommunication', 'differedCommunication', 'incompatibleTime');
    }
    return toolTipMessage;
  };

  const handleChangeDifferedCommunicationTime = (time: string) => {
    const isValidTime = (t: string) => !isTimeBefore(t, minDefferedTime);

    if (!time) {
      formik.setFieldValue('differedCommunicationTime', undefined);
      formik.setFieldError('differedCommunicationTime', undefined);
      return;
    }
    if (isValidTime(time)) {
      formik.setFieldValue('differedCommunicationTime', time);
      formik.setFieldError('differedCommunicationTime', undefined);
    } else {
      formik.setFieldError('differedCommunicationTime', 'INCOMPLETE_DIFFERED_ERROR');
    }
  };

  const handleChangeDifferedDaysBeforeCirculation = (event: ChangeEvent<HTMLInputElement>) => {
    const calculatedValue = Math.max(
      defferedDaysBeforeCirculationMin,
      Math.min(defferedDaysBeforeCirculationMax, Number(event.target.value))
    );
    formik.handleChange({
      target: { name: 'differedDaysBeforeCirculation', value: calculatedValue },
    });
    formik.setFieldValue('differedCommunicationTime', undefined);
  };

  return (
    <CardSendCommunication
      title={t('sendCommunication', 'communicationChannels', 'title')}
      elemRef={elemRef}
      style={{ marginTop: '1rem' }}
      wrraperStyle={{ paddingBottom: '40px' }}
    >
      <Row align="middle" style={{ width: '100%' }}>
        {hasTargetChannels && (
          <>
            <Row align="middle" style={{ maxWidth: '50%' }}>
              {channelsArePresent.mail && (
                <Checkbox
                  checked={formik.values.communicationChannels?.includes(SendCommunicationChannels.Mail)}
                  onChange={(e) => handleChannelChange(e, SendCommunicationChannels.Mail)}
                >
                  {t('sendCommunication', 'communicationChannels', 'mail')}
                </Checkbox>
              )}
              {channelsArePresent.sms && (
                <Checkbox
                  checked={formik.values.communicationChannels?.includes(SendCommunicationChannels.Sms)}
                  onChange={(e) => handleChannelChange(e, SendCommunicationChannels.Sms)}
                >
                  {t('sendCommunication', 'communicationChannels', 'sms')}
                </Checkbox>
              )}
              {channelsArePresent.whatsApp && (
                <Checkbox
                  checked={formik.values.communicationChannels?.includes(SendCommunicationChannels.WhatsApp)}
                  onChange={(e) => handleChannelChange(e, SendCommunicationChannels.WhatsApp)}
                >
                  {t('sendCommunication', 'communicationChannels', 'whatsApp')}
                </Checkbox>
              )}
              {channelsArePresent.flashInfo && (
                <Checkbox
                  checked={formik.values.communicationChannels?.includes(SendCommunicationChannels.FlashInfo)}
                  onChange={(e) => handleChannelChange(e, SendCommunicationChannels.FlashInfo)}
                >
                  {t('sendCommunication', 'communicationChannels', 'flashInfo')}
                </Checkbox>
              )}
            </Row>
            <StyledDivider type="vertical" height={targetChannelsHeight ? targetChannelsHeight - 40 : 0} />
          </>
        )}
        <Col>
          <SubTitle>{t('sendCommunication', 'differedCommunication', 'title')}</SubTitle>
          <ErrorMessage formik={formik} />
          <Row>
            <Radio.Group
              style={{ display: 'flex', flexDirection: 'column' }}
              value={formik.values.isDifferedCommunication}
              onChange={(e) => {
                formik.setFieldValue('isDifferedCommunication', e.target.value);
              }}
            >
              <Radio value={false}>{t('sendCommunication', 'differedCommunication', 'immediatLabel')}</Radio>
              <Tooltip title={renderDefferedTooltipMessage()} placement="bottom">
                <Radio value={true} disabled={disableDifferedCommunication}>
                  {t('sendCommunication', 'differedCommunication', 'differedLabel')}
                </Radio>
              </Tooltip>
            </Radio.Group>
            <Col style={{ top: '25px' }}>
              <NumberDaysInput
                name="differedDaysBeforeCirculation"
                value={formik.values.differedDaysBeforeCirculation}
                min={defferedDaysBeforeCirculationMin}
                max={defferedDaysBeforeCirculationMax}
                disabled={!formik.values.isDifferedCommunication}
                labelDisabled={disableDifferedCommunication}
                onChange={handleChangeDifferedDaysBeforeCirculation}
              />
              <TimeSelect
                minTime={minDefferedTime}
                value={formik.values.differedCommunicationTime}
                disabled={!formik.values.isDifferedCommunication}
                labelDisabled={disableDifferedCommunication}
                onChange={handleChangeDifferedCommunicationTime}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </CardSendCommunication>
  );
};

export default TargetChannels;
